/**
 * **수집 현황 대시보드 질문의 mockup 갈래** — `els-mock`(멘션)과 **같은 모양**이다.
 *
 * ```
 *   actions/els/moment.ts       collection.count · dailyCount · brandAccountDaily · brandAccountPreview ·
 *                               seedingCount · market.volume 머리에서 resolveMock*() 한 줄
 *   actions/els/influencer.ts   week.latest · week.newBreakdown 머리에서 같은 한 줄
 *     │  잡히면 픽스처, 아니면
 *     ▼
 *   /api/els/moments · /api/els/influencers   평소 그대로
 * ```
 *
 * ⚠️ 판정은 **「등록됐는가」** 하나다 — 이 질문들의 params 는 조회 조건(scope·계정·기간)뿐이라 `MOCKUP_` id 를
 * 실을 칸이 없다. 등록은 대시보드 mockup 라우트의 layout 이 마운트돼 있는 동안만이고 언마운트에 풀린다.
 * ⚠️ 핸들러가 `null` 을 주면 「내 것이 아니다」라는 뜻이고 호출은 평소대로 서버로 간다.
 * 등록이 없으면(기본) 모든 질문이 그대로 나간다 — 실화면 동작 무변.
 */
import type {
  InfluencerLatestWeekRequest,
  InfluencerLatestWeekResponse,
  InfluencerWeekBreakdownRequest,
  InfluencerWeekBreakdownResponse,
} from 'src/types/influencer/influencer-ops';
import type {
  MomentMarketVolumeRequest,
  MomentMarketVolumeResponse,
  MomentCollectionCountRequest,
  MomentCollectionCountResponse,
  MomentCollectionAccountRequest,
  MomentCollectionSeedingRequest,
  MomentCollectionSeedingResponse,
  MomentCollectionDailyCountRequest,
  MomentCollectionDailyCountResponse,
  MomentCollectionAccountDailyResponse,
  MomentCollectionAccountPreviewRequest,
  MomentCollectionAccountPreviewResponse,
} from 'src/types/moments/moment-ops';

export type DashboardMockHandlers = {
  /** 수집량 (`collection.count`) — 브랜드 멘션 정제 전 · 타겟 키워드 캠페인별 */
  collectionCount?: (params: MomentCollectionCountRequest) => MomentCollectionCountResponse | null;
  /** 수집량 + 일자별 (`collection.dailyCount`) — 브랜드 멘션 */
  collectionDailyCount?: (params: MomentCollectionDailyCountRequest) => MomentCollectionDailyCountResponse | null;
  /** 자사 계정 수집량 + 일자별 (`collection.brandAccountDaily`) */
  brandAccountDaily?: (params: MomentCollectionAccountRequest) => MomentCollectionAccountDailyResponse | null;
  /** 자사 계정 미리보기 (`collection.brandAccountPreview`) */
  brandAccountPreview?: (
    params: MomentCollectionAccountPreviewRequest,
  ) => MomentCollectionAccountPreviewResponse | null;
  /** 시딩 캠페인 수집 건수 (`collection.seedingCount`) */
  seedingCount?: (params: MomentCollectionSeedingRequest) => MomentCollectionSeedingResponse | null;
  /** 뷰티 시장 표본 (`market.volume`) */
  marketVolume?: (params: MomentMarketVolumeRequest) => MomentMarketVolumeResponse | null;
  /** 최신 발굴 주차 (`week.latest`) */
  influencerLatestWeek?: (params: InfluencerLatestWeekRequest) => InfluencerLatestWeekResponse | null;
  /** 주간 신규 발굴 분포 (`week.newBreakdown`) */
  influencerWeekBreakdown?: (params: InfluencerWeekBreakdownRequest) => InfluencerWeekBreakdownResponse | null;
};

let handlers: DashboardMockHandlers | null = null;

/** mockup 라우트만 부른다. `null` 이면 해제다 */
export function registerDashboardMock(next: DashboardMockHandlers | null): void {
  handlers = next;
}

export function resolveMockCollectionCount(
  params: MomentCollectionCountRequest,
): MomentCollectionCountResponse | null {
  return handlers?.collectionCount?.(params) ?? null;
}

export function resolveMockCollectionDailyCount(
  params: MomentCollectionDailyCountRequest,
): MomentCollectionDailyCountResponse | null {
  return handlers?.collectionDailyCount?.(params) ?? null;
}

export function resolveMockCollectionAccountDaily(
  params: MomentCollectionAccountRequest,
): MomentCollectionAccountDailyResponse | null {
  return handlers?.brandAccountDaily?.(params) ?? null;
}

export function resolveMockCollectionAccountPreview(
  params: MomentCollectionAccountPreviewRequest,
): MomentCollectionAccountPreviewResponse | null {
  return handlers?.brandAccountPreview?.(params) ?? null;
}

export function resolveMockCollectionSeedingCount(
  params: MomentCollectionSeedingRequest,
): MomentCollectionSeedingResponse | null {
  return handlers?.seedingCount?.(params) ?? null;
}

export function resolveMockMarketVolume(params: MomentMarketVolumeRequest): MomentMarketVolumeResponse | null {
  return handlers?.marketVolume?.(params) ?? null;
}

export function resolveMockInfluencerLatestWeek(
  params: InfluencerLatestWeekRequest,
): InfluencerLatestWeekResponse | null {
  return handlers?.influencerLatestWeek?.(params) ?? null;
}

export function resolveMockInfluencerWeekBreakdown(
  params: InfluencerWeekBreakdownRequest,
): InfluencerWeekBreakdownResponse | null {
  return handlers?.influencerWeekBreakdown?.(params) ?? null;
}
